import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { dayKey as studioDayKey } from '@/utils/studioDay';

/**
 * The reporting date filter: pick a single day or a range from a month calendar,
 * or use a quick preset. Emits day-keys ('YYYY-MM-DD'). Reuses the month-grid
 * pattern from MiniCalendarPopover but adds two-click range selection and presets.
 * Light-theme first; the sim clock (`todayIso`) is the "today" marker, never Date.now().
 */
interface Props {
  from: string;
  to: string;
  todayIso: string;
  onChange: (from: string, to: string) => void;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const dayKey = (d: Date) => format(d, 'yyyy-MM-dd');
const parseKey = (key: string) => new Date(`${key}T12:00:00`);

function shiftKey(key: string, deltaDays: number): string {
  return dayKey(new Date(parseKey(key).getTime() + deltaDays * 24 * 60 * 60_000));
}

function rangeLabel(from: string, to: string): string {
  const a = parseKey(from);
  const b = parseKey(to);
  if (from === to) return format(a, 'EEE d MMM');
  const sameYear = a.getFullYear() === b.getFullYear();
  const left = format(a, sameYear ? 'd MMM' : 'd MMM yyyy');
  return `${left} – ${format(b, 'd MMM yyyy')}`;
}

export default function ReportDateFilter({ from, to, todayIso, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const [month, setMonth] = useState(() => startOfMonth(parseKey(to)));
  const ref = useRef<HTMLDivElement>(null);
  const todayKey = studioDayKey(todayIso);

  useEffect(() => {
    if (open) {
      setMonth(startOfMonth(parseKey(to)));
      setPendingStart(null);
    }
  }, [open, to]);

  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', outside);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const gridDays = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [month]);

  const commit = (a: string, b: string) => {
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    onChange(lo, hi);
    setOpen(false);
    setPendingStart(null);
  };

  const onPick = (key: string) => {
    if (pendingStart === null) {
      setPendingStart(key); // first click: start of a new range
    } else {
      commit(pendingStart, key); // second click: close the range
    }
  };

  const presets: Array<{ label: string; from: string; to: string }> = [
    { label: 'Today', from: todayKey, to: todayKey },
    { label: 'Last 7 days', from: shiftKey(todayKey, -6), to: todayKey },
    { label: 'Last 14 days', from: shiftKey(todayKey, -13), to: todayKey },
    { label: 'Last 30 days', from: shiftKey(todayKey, -29), to: todayKey },
    { label: 'This month', from: dayKey(startOfMonth(parseKey(todayKey))), to: todayKey },
  ];

  // While mid-selection, preview the range from the pending start to today's hover
  // is not tracked; just highlight the committed range plus the pending start.
  const inRange = (key: string) => !pendingStart && key >= from && key <= to;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid="reporting-date-filter"
        className="inline-flex items-center gap-2 rounded-md border border-gold-dim/30 px-3 py-1.5 text-xs font-inter text-cream-soft hover:text-gold-bright hover:border-gold-dim/60 transition"
      >
        <CalendarDays size={13} aria-hidden="true" />
        <span data-testid="reporting-date-label">{rangeLabel(from, to)}</span>
      </button>
      {open ? (
        <div className="absolute right-0 mt-2 z-40 panel p-3 shadow-ink-soft flex gap-3" data-testid="reporting-date-popover">
          {/* Presets */}
          <div className="flex flex-col gap-1 w-28 shrink-0 border-r border-gold-dim/20 pr-3">
            {presets.map((p) => {
              const active = p.from === from && p.to === to;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => commit(p.from, p.to)}
                  data-testid={`reporting-date-preset-${p.label.toLowerCase().replace(/ /g, '-')}`}
                  className={clsx(
                    'text-left rounded-sm px-2 py-1.5 text-[11px] font-inter transition',
                    active ? 'bg-gold/15 text-gold-bright' : 'text-cream-soft hover:bg-ink-soft/60',
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Month grid */}
          <div className="w-64">
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setMonth((m) => addMonths(m, -1))}
                className="text-cream-soft hover:text-gold-bright transition"
                aria-label="Previous month"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="font-inter text-sm text-cream">{format(month, 'MMMM yyyy')}</span>
              <button
                type="button"
                onClick={() => setMonth((m) => addMonths(m, 1))}
                className="text-cream-soft hover:text-gold-bright transition"
                aria-label="Next month"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {WEEKDAYS.map((d) => (
                <span key={d} className="font-inter text-[9px] text-cream-soft uppercase tracking-widest py-1">
                  {d}
                </span>
              ))}
              {gridDays.map((day) => {
                const key = dayKey(day);
                const inMonth = isSameMonth(day, month);
                const isToday = isSameDay(day, parseKey(todayKey));
                const isEndpoint = key === from || key === to || key === pendingStart;
                const within = inRange(key);
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onClick={() => onPick(key)}
                    data-testid={`reporting-date-day-${key}`}
                    className={clsx(
                      'h-8 rounded-sm font-mono text-xs transition flex items-center justify-center',
                      isEndpoint
                        ? 'bg-gold/30 text-gold-bright'
                        : within
                          ? 'bg-gold/12 text-gold-bright'
                          : inMonth
                            ? 'text-cream hover:bg-ink-soft/60'
                            : 'text-cream-faint hover:bg-ink-soft/60',
                      isToday && !isEndpoint ? 'ring-1 ring-gold-bright/70' : '',
                    )}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
            <p className="font-inter text-[10px] text-cream-faint mt-2">
              {pendingStart ? 'Pick the range end…' : 'Click a day, or click a start then an end for a range.'}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
